import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Grid, 
  TextField, 
  Button, 
  Divider, 
  Card, 
  CardContent, 
  Chip, 
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  LocalPhone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
  const { user, updateUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    phone: '',
    location: '',
    role: '',
    bio: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Demo activity history
  const activityHistory = [
    { id: 1, date: '2023-06-15', action: 'Scanned 12 trees at Client A' },
    { id: 2, date: '2023-06-14', action: 'Updated profile information' },
    { id: 3, date: '2023-06-10', action: 'Scanned 8 trees at Client B' },
    { id: 4, date: '2023-06-05', action: 'Scanned 15 trees at Client C' }
  ];

  useEffect(() => {
    if (user) {
      // Initialize form with user data
      setFormData({
        username: user.username || '',
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
        location: user.location || '',
        role: user.role || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Update user profile
      updateUserProfile(formData);
      setSaveSuccess(true);
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    }
  };

  const renderReadOnlyProfile = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {formData.username.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Chip 
                label={formData.role} 
                color={formData.role === 'admin' ? 'error' : formData.role === 'superuser' ? 'secondary' : 'primary'} 
                size="small"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={8} md={9}>
            <Typography variant="h5" gutterBottom>
              {formData.fullName || formData.username}
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <EmailIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                {formData.email || 'No email provided'}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                {formData.phone || 'No phone provided'}
              </Typography>
              
              <Typography variant="body1">
                <LocationIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                {formData.location || 'No location provided'}
              </Typography>
            </Box>
            
            {formData.bio && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Bio
                </Typography>
                <Typography variant="body2" paragraph>
                  {formData.bio}
                </Typography>
              </>
            )}
            
            <Button 
              variant="outlined" 
              onClick={() => setIsEditing(true)}
              startIcon={<PersonIcon />}
            >
              Edit Profile
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderEditForm = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Edit Profile
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                margin="normal"
                disabled
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Save Changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>
      
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Profile updated successfully!
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {isEditing ? renderEditForm() : renderReadOnlyProfile()}
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Recent Activity
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <List>
          {activityHistory.map(activity => (
            <ListItem key={activity.id} divider>
              <ListItemIcon>
                <CalendarIcon />
              </ListItemIcon>
              <ListItemText 
                primary={activity.action} 
                secondary={activity.date} 
              />
            </ListItem>
          ))}
        </List>
      </Paper>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Account Security
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Password
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            sx={{ mb: 2 }}
            disabled
          >
            Change Password
          </Button>
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          Password change functionality will be available in a future update.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ProfilePage; 